import { Portal } from '@material-ui/core';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

// A one-element-at-a-time tour. The host has already navigated to the step's
// route; this waits for the step's target to render, scrolls it into view and
// draws a dimmed backdrop with a hole over it plus a small explanation card.
//
// The backdrop does not take pointer events, so the highlighted control stays
// usable: the point of "Show me" is that the user can try the feature there and
// then. Esc, "Skip tour" and "Done" end it.

const TARGET_TIMEOUT_MS = 4000;
const PADDING = 8;
const CARD_WIDTH = 320;
// Above MUI dialogs (1300) and the app's sticky headers, below MUI tooltips.
const Z_INDEX = 1400;

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export const findTarget = selector => {
  if (!selector) return null;
  try {
    const el = document.querySelector(selector);
    // An element in a collapsed section or the other breakpoint's nav has no box.
    return el && el.getClientRects().length > 0 ? el : null;
  } catch (e) {
    return null; // a bad selector in the catalogue must not take the page down
  }
};

// Below the target when there is room, otherwise above; clamped to the viewport.
export const placeCard = (rect, viewport, cardHeight = 180) => {
  const width = Math.min(CARD_WIDTH, viewport.width - 32);
  if (!rect) {
    return { width, left: (viewport.width - width) / 2, top: viewport.height - cardHeight - 24 };
  }
  const left = Math.min(Math.max(16, rect.left), viewport.width - width - 16);
  const below = rect.bottom + PADDING + 12;
  const top =
    below + cardHeight <= viewport.height ? below : Math.max(16, rect.top - PADDING - 12 - cardHeight);
  return { width, left, top };
};

export function TourSpotlight({ step, stepIndex, stepCount, onNext, onClose }) {
  const [target, setTarget] = useState(null);
  const [searching, setSearching] = useState(true);
  const [rect, setRect] = useState(null);
  const cardRef = useRef(null);
  const [cardHeight, setCardHeight] = useState(180);

  // Wait for the target: the route has only just changed and most pages fetch
  // before they render.
  useEffect(() => {
    setTarget(null);
    setRect(null);
    setSearching(true);
    if (!step.target) {
      setSearching(false);
      return undefined;
    }
    const started = Date.now();
    const timer = setInterval(() => {
      const el = findTarget(step.target);
      if (el || Date.now() - started > TARGET_TIMEOUT_MS) {
        clearInterval(timer);
        setTarget(el);
        setSearching(false);
        if (el) el.scrollIntoView({ block: 'center', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
      }
    }, 100);
    return () => clearInterval(timer);
  }, [step]);

  const measure = useCallback(() => {
    setRect(target ? target.getBoundingClientRect() : null);
  }, [target]);

  // Re-measure when the page scrolls (any scroller, hence capture) or resizes,
  // batched to one measurement per frame.
  useEffect(() => {
    measure();
    let frame = null;
    const schedule = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = null;
        measure();
      });
    };
    window.addEventListener('resize', schedule);
    window.addEventListener('scroll', schedule, true);
    return () => {
      window.removeEventListener('resize', schedule);
      window.removeEventListener('scroll', schedule, true);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [measure]);

  useLayoutEffect(() => {
    if (cardRef.current) setCardHeight(cardRef.current.offsetHeight);
  }, [step, rect, searching]);

  useEffect(() => {
    const onKey = event => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (searching) return null;

  const viewport = { width: window.innerWidth, height: window.innerHeight };
  const card = placeCard(rect, viewport, cardHeight);
  const isLast = stepIndex >= stepCount - 1;

  return (
    <Portal>
      {rect ? (
        <div
          aria-hidden
          data-testid="whats-new-spotlight"
          style={{
            position: 'fixed',
            top: rect.top - PADDING,
            left: rect.left - PADDING,
            width: rect.width + PADDING * 2,
            height: rect.height + PADDING * 2,
            borderRadius: 16,
            boxShadow: '0 0 0 9999px rgba(15, 23, 42, 0.55)',
            outline: '2px solid #6366f1',
            pointerEvents: 'none',
            zIndex: Z_INDEX,
            transition: prefersReducedMotion() ? 'none' : 'top 150ms ease-out, left 150ms ease-out'
          }}
        />
      ) : (
        <div
          aria-hidden
          style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.35)', pointerEvents: 'none', zIndex: Z_INDEX }}
        />
      )}
      <div
        ref={cardRef}
        role="dialog"
        aria-modal="false"
        aria-labelledby="whats-new-tour-title"
        data-testid="whats-new-tour-card"
        className="bg-white rounded-2xl shadow-xl border border-solid border-slate-200 p-4"
        style={{ position: 'fixed', top: card.top, left: card.left, width: card.width, zIndex: Z_INDEX + 1 }}>
        <p id="whats-new-tour-title" className="text-sm font-black text-slate-900 m-0">
          {step.title}
        </p>
        <p className="text-sm text-slate-600 leading-relaxed m-0 mt-1">{step.body}</p>
        {step.target && !target && (
          <p className="text-xs text-slate-400 m-0 mt-2">
            It isn&apos;t showing on this page for your account right now.
          </p>
        )}
        <div className="flex items-center justify-between gap-2 mt-3">
          <span className="text-xs text-slate-400 font-semibold">
            {stepCount > 1 ? `${stepIndex + 1} of ${stepCount}` : ''}
          </span>
          <div className="flex items-center gap-2">
            {!isLast && (
              <button
                type="button"
                onClick={onClose}
                className="border-0 bg-transparent cursor-pointer text-sm font-semibold text-slate-500 hover:text-slate-700 px-2 py-1">
                Skip tour
              </button>
            )}
            <button
              type="button"
              autoFocus
              onClick={isLast ? onClose : onNext}
              className="border-0 cursor-pointer bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-sm px-4 py-2 rounded-xl active:translate-y-px focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2">
              {isLast ? 'Done' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </Portal>
  );
}

TourSpotlight.propTypes = {
  step: PropTypes.shape({
    route: PropTypes.string.isRequired,
    target: PropTypes.string,
    title: PropTypes.string.isRequired,
    body: PropTypes.string.isRequired
  }).isRequired,
  stepIndex: PropTypes.number.isRequired,
  stepCount: PropTypes.number.isRequired,
  onNext: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired
};

export default TourSpotlight;
